// FILE: GenerationDefaultsSection.tsx
// Purpose: Settings → Advanced → Generation defaults: which model the server
//          uses for its own short-form writing, for the whole installation.
// Layer: Web settings presentation
// Exports: GenerationDefaultsSection

import { useCallback, useEffect, useState } from "react";

import { getAppModelOptions, useAppSettings } from "~/appSettings";
import { SystemSettingsClient } from "~/lib/systemSettingsClient";

import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";

/**
 * The model behind generated commit messages, PR text, worktree branch names and
 * thread titles.
 *
 * This used to be a per-user preference in `appSettings`, and it never really
 * worked as one: two of its four uses happen server-side after a turn, with no
 * browser to read a local value from, so the same installation generated titles
 * with one model and commit messages with another depending on who clicked. It is
 * now a single stored value (`system_settings`, key `generation-defaults`) that an
 * admin owns.
 *
 * Codex-only, matching the one implementation of `TextGenerationService`. The
 * option list is the Codex catalog plus the custom slugs the viewer has saved
 * locally — those are still a per-browser list, so an admin who wants a slug the
 * catalog lacks adds it under Settings → Models first.
 *
 * No optimistic update: the select reflects the server's answer, so a failed save
 * leaves the old value visible with the reason next to it rather than showing a
 * setting that is not in force.
 */
export function GenerationDefaultsSection() {
  const { settings } = useAppSettings();
  const [smallModel, setSmallModel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void SystemSettingsClient.readGenerationDefaults()
      .then((value) => {
        if (!cancelled) setSmallModel(value.smallModel);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // Reading needs `a.view`. Someone without it is not an admin for this
        // purpose, so the section says why rather than showing an empty select.
        setError(
          cause instanceof Error ? cause.message : "Could not load the generation defaults.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async (nextModel: string) => {
    setIsSaving(true);
    setError(null);
    try {
      const stored = await SystemSettingsClient.writeGenerationDefaults({ smallModel: nextModel });
      setSmallModel(stored.smallModel);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not save the change.");
    } finally {
      setIsSaving(false);
    }
  }, []);

  if (error !== null && smallModel === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        {error}
      </p>
    );
  }

  if (smallModel === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        Loading generation defaults…
      </p>
    );
  }

  const options = getAppModelOptions("codex", settings.customCodexModels, smallModel);
  const selectedLabel = options.find((option) => option.slug === smallModel)?.name ?? smallModel;

  return (
    <div className="space-y-3 rounded-xl border border-border/70 px-4 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">Git writing model</p>
          <p className="text-xs text-muted-foreground">
            Commit messages, PR titles and bodies, worktree branch names and thread titles. One
            value for everyone on this server.
          </p>
        </div>
        <Select
          value={smallModel}
          onValueChange={(value) => {
            if (!value || value === smallModel) return;
            void save(value);
          }}
        >
          <SelectTrigger
            className="w-full sm:w-52"
            disabled={isSaving}
            aria-label="Git writing model"
          >
            <SelectValue>{selectedLabel}</SelectValue>
          </SelectTrigger>
          <SelectPopup align="end" alignItemWithTrigger={false}>
            {options.map((option) => (
              <SelectItem hideIndicator key={option.slug} value={option.slug}>
                {option.name}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      </div>
      {error !== null ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
